import { type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown } from 'lucide-react'
import { Badge, Button, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/v2/ui'
import type { PackshotType } from '@/types/products'

interface PackshotActionCellProps {
  packshot_score?: number
  packshot_type?: PackshotType
  packshot_assigned?: boolean
  productName: string
  onAssign?: () => void
}

function scoreVariant(score: number, type?: PackshotType) {
  if (score >= 70 && type === 'PACKSHOT') return 'success' as const
  if (score >= 40) return 'warning' as const
  return 'error' as const
}

export function PackshotActionCell({ packshot_score, packshot_type, packshot_assigned, productName, onAssign }: PackshotActionCellProps) {
  const { t } = useTranslation()
  const stop = (e: MouseEvent) => e.stopPropagation()

  if (packshot_assigned) {
    return <Check className="packshot-action-cell h-5 w-5 text-emerald-500" aria-label={t('products.packshot_assigned')} />
  }

  if (packshot_score === undefined) {
    return <span className="packshot-action-cell text-sm text-neutral-400">{t('products.packshot_analysis_pending')}</span>
  }

  const isReady = packshot_score >= 70 && packshot_type === 'PACKSHOT'

  return (
    <div className="packshot-action-cell flex items-center gap-2" onClick={stop}>
      <div className="flex flex-col items-center">
        <Badge variant={scoreVariant(packshot_score, packshot_type)} className="text-xs tabular-nums w-9 justify-center">
          {packshot_score}
        </Badge>
        <span className="text-xs text-neutral-400 mt-0.5">{packshot_type ?? 'OFFEN'}</span>
      </div>
      {isReady ? (
        <Button variant="primary" size="sm" onClick={onAssign} aria-label={`${t('products.packshot_set_as')}: ${productName}`}>
          <Check className="w-3 h-3 mr-1" />{t('products.packshot_set_as')}
        </Button>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" aria-label={`${t('products.packshot_options')} ${productName}`}>
              {t('products.packshot_options')} <ChevronDown className="w-3 h-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem>{t('products.packshot_manual_review')}</DropdownMenuItem>
            <DropdownMenuItem>{t('products.packshot_analyze')}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
